import { formatMoney } from "@/utils/billingFormat";

export const BOOKER_TYPE_OPTIONS = [
  { value: 1, label: "Travel agency" },
  { value: 2, label: "Corporate client" },
  { value: 3, label: "Tour operator" },
  { value: 4, label: "Event organizer" },
  { value: 5, label: "Individual booker" },
  { value: 6, label: "Online sales partner" },
  { value: 7, label: "Partner business" },
  { value: 8, label: "Other" },
];

export const BOOKER_STATUS_OPTIONS = [
  { value: 1, label: "Pending" },
  { value: 2, label: "Active" },
  { value: 3, label: "Suspended" },
  { value: 4, label: "Archived" },
];

export const BOOKER_REWARD_TRIGGER_OPTIONS = [
  { value: 1, label: "Booking confirmed" },
  { value: 2, label: "Guest checked in" },
  { value: 3, label: "Bill paid" },
  { value: 4, label: "Checkout completed" },
];

export const BOOKER_REWARD_BASIS_OPTIONS = [
  { value: 1, label: "Room revenue" },
  { value: 2, label: "Total bill" },
  { value: 3, label: "Room nights" },
  { value: 4, label: "Booking count" },
];

export const BOOKER_REWARD_CURRENCY_OPTIONS = [
  { value: 1, label: "Points" },
  { value: 2, label: "Credit" },
  { value: 3, label: "Coupon" },
];

export const getOptionLabel = (options, value, fallback = "—") => {
  const normalized = String(value ?? "").replace(/[\s_-]/g, "").toLowerCase();
  const match = options.find(
    (item) =>
      String(item.value) === String(value) ||
      item.label.replace(/\s/g, "").toLowerCase() === normalized
  );
  return match?.label || fallback;
};

export const getApiData = (response) =>
  response?.data?.data || response?.data?.Data || response?.data || null;

export const getApiMessage = (error, fallback = "Something went wrong.") =>
  error?.response?.data?.message ||
  error?.response?.data?.Message ||
  error?.response?.data?.title ||
  error?.message ||
  fallback;

export const getPagedItems = (response) => {
  const data = getApiData(response) || {};
  return data.items || data.Items || data.data || data.Data || (Array.isArray(data) ? data : []);
};

export const getPagedTotal = (response) => {
  const data = getApiData(response) || {};
  return Number(data.totalRecords ?? data.TotalRecords ?? data.total ?? data.Total ?? 0);
};

export const getBookerTypeLabel = (value) => {
  const normalized = String(value ?? "").replace(/[\s_-]/g, "").toLowerCase();
  const match = BOOKER_TYPE_OPTIONS.find(
    (item) =>
      String(item.value) === String(value) ||
      item.label.replace(/\s/g, "").toLowerCase() === normalized
  );
  return match?.label || String(value || "Other").replace(/([a-z])([A-Z])/g, "$1 $2");
};

export const getBookerStatusLabel = (value) => {
  const normalized = String(value ?? "").replace(/[\s_-]/g, "").toLowerCase();
  const match = BOOKER_STATUS_OPTIONS.find(
    (item) =>
      String(item.value) === String(value) ||
      item.label.replace(/\s/g, "").toLowerCase() === normalized
  );
  return match?.label || String(value || "Active").replace(/([a-z])([A-Z])/g, "$1 $2");
};

export const getBookerRewardTriggerLabel = (value) =>
  getOptionLabel(BOOKER_REWARD_TRIGGER_OPTIONS, value, "Bill paid");

export const getBookerRewardBasisLabel = (value) =>
  getOptionLabel(BOOKER_REWARD_BASIS_OPTIONS, value, "Room revenue");

export const getBookerRewardCurrencyLabel = (value) =>
  getOptionLabel(BOOKER_REWARD_CURRENCY_OPTIONS, value, "Points");

export const getBookerStatusBadgeClass = (status) => {
  const label = getBookerStatusLabel(status);
  if (label === "Suspended") return "border-red-200 bg-red-50 text-red-700";
  if (label === "Archived") return "border-slate-200 bg-slate-50 text-slate-700";
  if (label === "Pending") return "border-amber-200 bg-amber-50 text-amber-800";
  return "border-emerald-200 bg-emerald-50 text-emerald-700";
};

export const formatBookerDate = (value) => {
  if (!value) return "—";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "—";
  return new Intl.DateTimeFormat("en-GB").format(date);
};

export const normalizeBooker = (booker = {}) => ({
  id: booker.id || booker.Id,
  partnerBusinessId: booker.partnerBusinessId || booker.PartnerBusinessId || "",
  partnerBusinessName: booker.partnerBusinessName || booker.PartnerBusinessName || "",
  bookerType: Number(booker.bookerType ?? booker.BookerType ?? 5),
  name: booker.name || booker.Name || "",
  legalName: booker.legalName || booker.LegalName || "",
  contactPersonName: booker.contactPersonName || booker.ContactPersonName || "",
  email: booker.email || booker.Email || "",
  phoneNumber: booker.phoneNumber || booker.PhoneNumber || "",
  address: booker.address || booker.Address || "",
  country: booker.country || booker.Country || "",
  taxId: booker.taxId || booker.TaxId || "",
  registrationNumber: booker.registrationNumber || booker.RegistrationNumber || "",
  website: booker.website || booker.Website || "",
  status: Number(booker.status ?? booker.Status ?? 2),
  totalBookings: Number(booker.totalBookings ?? booker.TotalBookings ?? 0),
  totalRoomNights: Number(booker.totalRoomNights ?? booker.TotalRoomNights ?? 0),
  totalRevenueBrought: Number(booker.totalRevenueBrought ?? booker.TotalRevenueBrought ?? 0),
  totalRewardPoints: Number(booker.totalRewardPoints ?? booker.TotalRewardPoints ?? 0),
  availableRewardPoints: Number(booker.availableRewardPoints ?? booker.AvailableRewardPoints ?? 0),
  lastBookingAt: booker.lastBookingAt || booker.LastBookingAt || "",
  createdAt: booker.createdAt || booker.CreatedAt || "",
});

export { formatMoney };
